import React, { useEffect, useState } from "react";
import DashboardLayout from "@/components/DashboardLayout";
import ConfirmDialog from "@/components/ConfirmDialog";
import { api } from "@/lib/api";
import { copyToClipboard } from "@/lib/clipboard";
import { toPreviewShareUrl } from "@/lib/shareUrl";
import { Link as RouterLink, useNavigate } from "react-router-dom";
import { Copy, Trash2, ExternalLink, BarChart3 } from "lucide-react";
import { toast } from "sonner";

export default function LinksList() {
  const [links, setLinks] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null); // {link_id, name}
  const [deleting, setDeleting] = useState(false);
  const navigate = useNavigate();

  const load = async () => {
    const { data } = await api.get("/links");
    setLinks(data);
  };

  useEffect(() => { load(); }, []);

  const requestDelete = (link) => setPendingDelete({ link_id: link.link_id, name: link.name });

  const confirmDelete = async () => {
    if (!pendingDelete) return;
    setDeleting(true);
    try {
      await api.delete(`/links/${pendingDelete.link_id}`);
      toast.success("Link deleted");
      setPendingDelete(null);
      await load();
    } catch {
      toast.error("Failed to delete link");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="flex items-center justify-between mb-8">
        <div>
          <div className="label-eyebrow text-[#002FA7]">Your links</div>
          <h1 className="text-4xl tracking-tight font-black text-[#111827]">All links</h1>
        </div>
        <RouterLink to="/dashboard/links/new" data-testid="links-create-cta" className="btn-primary">+ New link</RouterLink>
      </div>

      {links === null ? (
        <div className="card-flat h-40 animate-pulse bg-[#F9FAFB]" />
      ) : links.length === 0 ? (
        <div className="card-flat p-10 text-center">
          <div className="font-display font-extrabold text-2xl mb-2">No links yet</div>
          <p className="text-[#4B5563] mb-6">Create your first trackable affiliate link — your name will appear inside the URL.</p>
          <RouterLink to="/dashboard/links/new" data-testid="links-empty-cta" className="btn-primary">Create your first link</RouterLink>
        </div>
      ) : (
        <div className="space-y-3" data-testid="links-list">
          {links.map((l) => (
            <div key={l.link_id} className="card-flat p-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div className="min-w-0 flex-1">
                  <div className="font-display font-extrabold text-lg text-[#111827] truncate">{l.name}</div>
                  <div className="mt-1 flex items-center gap-2 flex-wrap">
                    <code className="font-mono text-xs bg-[#F3F4F6] rounded px-2 py-1 break-all">{toPreviewShareUrl(l.public_url)}</code>
                    <button onClick={() => copyToClipboard(toPreviewShareUrl(l.public_url), "Link with preview copied")} data-testid={`copy-link-${l.link_id}`} className="inline-flex items-center gap-1 text-xs font-bold text-[#002FA7] hover:underline">
                      <Copy size={12} /> Copy
                    </button>
                    <a href={toPreviewShareUrl(l.public_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-bold text-[#4B5563] hover:text-[#111827]">
                      <ExternalLink size={12} /> Open
                    </a>
                  </div>
                  <div className="mt-2 text-xs text-[#4B5563]">→ <span className="text-[#111827]">{l.destination_url}</span></div>
                </div>
                <div className="flex items-center gap-6">
                  <Stat label="Clicks" value={l.total_clicks} />
                  <Stat label="Leads" value={l.total_leads} />
                  <Stat label="Conv." value={l.total_conversions || 0} accent />
                  <button onClick={() => navigate(`/dashboard/links/${l.link_id}`)} data-testid={`view-link-${l.link_id}`} className="btn-secondary !py-2 !px-3"><BarChart3 size={14} /> Analytics</button>
                  <button onClick={() => requestDelete(l)} data-testid={`delete-link-${l.link_id}`} className="text-[#EF4444] hover:bg-[#FEF2F2] p-2 rounded-md"><Trash2 size={16} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <ConfirmDialog
        open={!!pendingDelete}
        title="Delete this link?"
        description={pendingDelete ? `"${pendingDelete.name}" — along with all its click and lead history — will be permanently removed. This cannot be undone.` : ""}
        confirmLabel="Delete link"
        busy={deleting}
        onCancel={() => !deleting && setPendingDelete(null)}
        onConfirm={confirmDelete}
      />
    </DashboardLayout>
  );
}

function Stat({ label, value, accent }) {
  return (
    <div className="text-right">
      <div className="label-eyebrow text-[#4B5563]">{label}</div>
      <div className={`font-display font-black text-2xl ${accent ? "text-[#002FA7]" : "text-[#111827]"}`}>{value}</div>
    </div>
  );
}
